import React, { useState } from 'react';
import { isNil, orderBy } from 'lodash/fp';
import { useUser } from '../../../../utils/hooks/auth';
import { SortOrder, useCategoriesStatisticsQuery } from '../../../../generated/graphql';
import { Ordering } from '../../../../ui-kit';
import { useFromToContext } from '../../../shared';
import { TableWithCollapsibleContent } from '../style';
import { getHeadCells } from './utils';
import TableContent from './TableContent';
import { OrderColumn } from './types';
import TableHeadCells from './TableHead';

const CategoryStatisticsTable = () => {
    const [ordering, setOrdering] = useState<[OrderColumn | null, SortOrder | false]>(['grossRevenue', SortOrder.Desc]);
    const { user: { venueType } } = useUser();
    const headCells = getHeadCells(venueType);
    const { dateFromTo } = useFromToContext();

    const { data } = useCategoriesStatisticsQuery({
        variables: { dateFromTo }
    });
    const categories = data ? data.categoriesStatistics : [];

    const [orderingColumn, orderDirection] = ordering;
    const markupSort = orderingColumn === 'netMargin' ? orderDirection : false;
    const foodCostSort = orderingColumn === 'foodCost' ? orderDirection : false;
    const netRevenueSort = orderingColumn === 'netRevenue' ? orderDirection : false;
    const quantitySort = orderingColumn === 'quantity' ? orderDirection : false;
    const revenueSort = orderingColumn === 'grossRevenue' ? orderDirection : false;

    const setNewSort = (column: OrderColumn) => (sortDirection: SortOrder | false) => {
        setOrdering([sortDirection ? column : null, sortDirection]);
    };

    const setMarkupSort = setNewSort('netMargin');
    const setFoodCostSort = setNewSort('foodCost');
    const setNetRevenueSort = setNewSort('netRevenue');
    const setQuantitySort = setNewSort('quantity');
    const setRevenueSort = setNewSort('grossRevenue');

    const finalOrderDirection = orderDirection ? orderDirection.toLowerCase() as Ordering : 'asc';
    const finalOrderingColumn = orderingColumn || 'name';

    const orderedItems = orderBy(item => {
        const { [finalOrderingColumn]: orderColumn } = item;
        if (isNil(orderColumn)) {
            return finalOrderDirection === 'asc' ? 999999999 : -999999999;
        }
        return orderColumn;

    }, finalOrderDirection, categories);

    return (
        <TableWithCollapsibleContent
            fixedLayout
            isFirstColumnSticky
            customHeadCellProps={{
                markupSort, setMarkupSort,
                foodCostSort, setFoodCostSort,
                netRevenueSort, setNetRevenueSort,
                quantitySort, setQuantitySort,
                revenueSort, setRevenueSort,
            }}
            TableHeadCellComponent={TableHeadCells}
            headCells={headCells}
        >
            <TableContent items={orderedItems} />
        </TableWithCollapsibleContent>
    );
};

export default CategoryStatisticsTable;
